import React from "react";
import {
  Box,
  Checkbox,
  Chip,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useFormikContext } from "formik";

import FieldGroup from "../../../../shared/FieldGroup";
import {
  INTERVENTION_STRATEGY_OPTIONS_BY_TARGET_TYPE,
  type TargetFormValues,
  type TeachingProcedure,
} from "../../TargetBuilderPanel.types";

/**
 * Common Teaching Procedure fields rendered above every per-L3 procedure form.
 *
 * Owns:
 *   - `intervention_strategies` (multi-select; options depend on `target_type`)
 *   - `intervention_instructions` (free text, optional)
 *
 * Source of truth: `values.teaching_procedure` (read/write via setFieldValue).
 * Renders nothing when there's no active procedure.
 */
const TeachingProcedureCommonFields: React.FC = () => {
  const { values, setFieldValue } = useFormikContext<TargetFormValues>();
  const procedure = values.teaching_procedure;
  if (!procedure) return null;

  const strategies = procedure.intervention_strategies ?? [];
  const instructions = procedure.intervention_instructions ?? "";
  const options = INTERVENTION_STRATEGY_OPTIONS_BY_TARGET_TYPE[values.target_type] ?? [];

  const update = (partial: Partial<TeachingProcedure>) =>
    setFieldValue("teaching_procedure", { ...procedure, ...partial });

  return (
    <Stack gap={5}>
      <FieldGroup
        label="Intervention Strategies"
        description="Select the teaching approaches that will be used for this target"
        required
      >
        <Stack gap={2}>
          <Typography variant="caption" fontWeight={500} color="text.secondary">
            Select teaching approaches:
          </Typography>
          <Select
            size="small"
            fullWidth
            multiple
            value={strategies}
            onChange={(e) =>
              update({
                intervention_strategies:
                  typeof e.target.value === "string"
                    ? e.target.value.split(",")
                    : (e.target.value as string[]),
              })
            }
            displayEmpty
            aria-label="Intervention strategies"
            renderValue={(selected) => {
              const arr = selected as string[];
              if (!arr || arr.length === 0) {
                return (
                  <Typography variant="body2" color="text.disabled">
                    Select strategies...
                  </Typography>
                );
              }
              return (
                <Typography variant="body2">
                  {arr.length} of {options.length} selected
                </Typography>
              );
            }}
            MenuProps={{ PaperProps: { sx: { maxHeight: 360 } } }}
          >
            {options.map((opt) => (
              <MenuItem key={opt} value={opt}>
                <Checkbox size="small" checked={strategies.includes(opt)} />
                <ListItemText primary={opt} />
              </MenuItem>
            ))}
          </Select>

          {strategies.length > 0 && (
            <Stack gap={1} sx={{ borderTop: 1, borderColor: "divider", mt: 3, pt: 2 }}>
              <Typography variant="caption" fontWeight={500} color="text.secondary">
                Selected Strategies ({strategies.length}):
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {strategies.map((value) => (
                  <Chip
                    key={value}
                    size="small"
                    label={value}
                    onDelete={() =>
                      update({
                        intervention_strategies: strategies.filter((s) => s !== value),
                      })
                    }
                    sx={{
                      bgcolor: "primary.main",
                      color: "common.white",
                      fontWeight: 600,
                      fontSize: 12,
                      "& .MuiChip-deleteIcon": {
                        color: "common.white",
                        fontSize: 16,
                        "&:hover": { color: "rgba(255,255,255,0.7)" },
                      },
                    }}
                  />
                ))}
              </Box>
            </Stack>
          )}
        </Stack>
      </FieldGroup>

      <FieldGroup
        label="Intervention Instructions"
        description="Describe how the selected strategies should be implemented in practice"
      >
        <TextField
          multiline
          minRows={3}
          fullWidth
          value={instructions}
          onChange={(e) => update({ intervention_instructions: e.target.value })}
          placeholder="e.g., 'Use BST across all sessions; fade prompts after 3 consecutive independent responses'"
          aria-label="Intervention instructions"
        />
      </FieldGroup>

      <FieldGroup
        label="Plan for Generalization"
        description="What settings, people, or materials should be varied to promote generalization?"
        required
      >
        <TextField
          multiline
          minRows={3}
          fullWidth
          value={procedure.generalization_plan ?? ""}
          onChange={(e) => update({ generalization_plan: e.target.value })}
          placeholder="e.g., 'Practice across different settings, with various instructors, using novel materials'"
          aria-label="Plan for generalization"
        />
      </FieldGroup>
    </Stack>
  );
};

export default TeachingProcedureCommonFields;
